import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@v2/components/ui/avatar';
import { getInitials } from '@gettactic/helpers/src';
import { IUserFull } from '@gettactic/api';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '@gettactic/helpers';
import { getRandomColor } from '@gettactic/helpers/src/colors';
import { getContrastTextColor } from '@gettactic/helpers/src/colors/getContrastTextColor';
import Image from 'next/image';

export const avatarVariants = cva('rounded-full', {
  variants: {
    size: {
      xs: 'h-5 w-5 text-[10px]',
      s: 'h-6 w-6 text-[10px]',
      sm: 'h-8 w-8',
      md: 'h-10 w-10',
      lg: 'h-14 w-14',
      xl: 'h-[70px] w-[70px]',
      '2xl': 'h-24 w-24 sm:h-32 sm:w-32',
      'full': 'h-full w-full'
    }
  },
  defaultVariants: {
    size: 'xl'
  }
});

export interface UserAvatarProps extends VariantProps<typeof avatarVariants> {
  user: Partial<IUserFull>;
  className?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = React.memo(({
  user,
  size,
  className,
}) => {
  const initials = getInitials(user.name || '');
  let bgColor = initials
    ? getRandomColor({ string: user.name || '' })
    : undefined;
  
  const isDeleted = user.roles?.includes('deleted');
  if (isDeleted) {
    bgColor = 'gray';
  }
  
  return (
    <Avatar className={cn(avatarVariants({ size }), { 'grayscale': isDeleted }, className)}>
      {!!user.profile_photo_url && <Image layout='fill' src={user.profile_photo_url ?? ''} className={isDeleted ? 'grayscale' : ''} />}
      <AvatarFallback
        style={
          initials && bgColor
            ? {
                backgroundColor: bgColor,
                color: getContrastTextColor(bgColor)
              }
            : undefined
        }
      >
        {getInitials(user.name || '')}
      </AvatarFallback>
    </Avatar>
  );
}, (prevProps, nextProps) => {
  return prevProps.size === nextProps.size &&
    prevProps.user?.profile_photo_url === nextProps.user?.profile_photo_url &&
    prevProps.user?.name === nextProps.user?.name &&
    prevProps.user?.id === nextProps.user?.id;
});
